import React, { useState, useEffect, useCallback, useRef } from "react";
import OutputsSummaryTable from "./OutputsSummaryTable";
import { 
    FormControl,
    FormControlLabel,
    MenuItem,
    Select,
    Skeleton,
    Grid,
    Paper,
    InputLabel,
    Box,
    Tabs,
    Tab,
    Switch,
    CircularProgress,
    ToggleButtonGroup,
    ToggleButton
} from "@mui/material";
import ForecastDropdown from "./ForecastDropdown";
import ForecastPeriodDropdown from "./ForecastPeriodDropdown";
import ProductFiltersButton from "./ProductFiltersButton";
import ProductFilters from "./ProductFilters";
import CompareButton from "./CompareButton";
import CompareFilters from "./CompareFilters";
import axiosInstance from "../AxiosInstance";

const GROUP_BY_STORAGE_KEY = 'abakus.outputsSummaryTable.groupBy';
const ACTUALS_MODE_VIEWS = new Set(["units", "grossRev", "triggers", "overlay", "cogs", "logs"]);
const supportsActualsMode = (selectedView) => ACTUALS_MODE_VIEWS.has(selectedView);
const ACTUALS_CHECKPOINT_MODES = {
    now: "now",
    latestOrder: "latestOrder"
};

const ForecastOutputsSummary = () => {
    const [forecasts, setForecasts] = useState([]);
    const [view, setView] = useState("units");
    const [aggregationPeriod, setAggregationPeriod] = useState("monthly");
    const [groupBy, setGroupBy] = useState(() => {
        try {
            const stored = localStorage.getItem(GROUP_BY_STORAGE_KEY);
            return stored === "sku" || stored === "customer" || stored === "list" ? stored : "sku";
        } catch (error) {
            return "sku";
        }
    });
    const [selectedForecastId, setSelectedForecastId] = useState(null);
    const [selectedForecastPeriodId, setSelectedForecastPeriodId] = useState(null);
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [selectedProductGroups, setSelectedProductGroups] = useState([]);
    const [summaryData, setSummaryData] = useState({ channels: [] });
    const [ytgSourceData, setYtgSourceData] = useState(null);
    const [loadingForecasts, setLoadingForecasts] = useState(true);
    const [loadingTable, setLoadingTable] = useState(false);
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [showActuals, setShowActuals] = useState(false);
    const [actualsCheckpointMode, setActualsCheckpointMode] = useState(ACTUALS_CHECKPOINT_MODES.now);
    const [loadingActuals, setLoadingActuals] = useState(false);
    const [showCompare, setShowCompare] = useState(false);
    const [compareExpanded, setCompareExpanded] = useState(false);
    const [compareForecastId, setCompareForecastId] = useState(null);
    const [compareForecastPeriodId, setCompareForecastPeriodId] = useState(null);
    const [comparisonData, setComparisonData] = useState(null);
    const [comparisonYtgSourceData, setComparisonYtgSourceData] = useState(null);
    const [loadingComparison, setLoadingComparison] = useState(false);
    const [isTableFullscreen, setIsTableFullscreen] = useState(false);
    const previousShowActualsRef = useRef(showActuals);
    const viewSupportsActualsMode = supportsActualsMode(view);
    const isForecastCompareEnabled = !!(compareExpanded && compareForecastId && compareForecastPeriodId !== null);
    const selectedForecast = forecasts.find((f) => Number(f.forecastId) === Number(selectedForecastId));
    const comparisonForecast = forecasts.find((f) => Number(f.forecastId) === Number(compareForecastId));
    const isForecastStarted = !selectedForecast?.startDate || new Date(selectedForecast.startDate) <= new Date();
    const hasValidSelection = !!(
        selectedForecastId &&
        selectedForecastPeriodId !== null &&
        selectedForecastPeriodId > 0
    );

    const handleFiltersToggle = () => {
        setFiltersExpanded(prev => !prev);
    };

    const handleCompareToggle = () => {
        setCompareExpanded(prev => {
            const newExpanded = !prev;
            if (!newExpanded) {
                setCompareForecastId(null);
                setCompareForecastPeriodId(null);
                setComparisonData(null);
                if (showActuals) {
                    setShowActuals(false);
                }
            } else {
                if (showActuals) {
                    setShowActuals(false);
                    setShowCompare(false);
                    setLoadingActuals(false);
                }
            }
            return newExpanded;
        });
    };

    const handleCompareForecastSelected = (id) => {
        const parsedId = Number(id);
        setCompareForecastId(Number.isFinite(parsedId) ? parsedId : id);
        setCompareForecastPeriodId(null);
    };

    const handleCompareForecastPeriodSelected = (forecastPeriodId) => {
        setCompareForecastPeriodId(forecastPeriodId);
    };

    useEffect(() => {
        const storedForecast = JSON.parse(localStorage.getItem("selectedForecast"));
        const storedForecastPeriodId = localStorage.getItem("selectedForecastPeriodId");

        if (storedForecast?.forecastId) {
            const parsedForecastId = Number(storedForecast.forecastId);
            setSelectedForecastId(Number.isFinite(parsedForecastId) ? parsedForecastId : storedForecast.forecastId);
        }

        if (storedForecastPeriodId && storedForecastPeriodId !== "null") {
            setSelectedForecastPeriodId(parseInt(storedForecastPeriodId, 10));
        } else {
            setSelectedForecastPeriodId(null);
        }

        fetchForecastsAndPeriods();
    }, []);

    const fetchForecastsAndPeriods = async () => {
        try {
            setLoadingForecasts(true);
            const response = await axiosInstance.get("/forecasts");
            setForecasts(response.data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingForecasts(false);
        }
    };

    const fetchSummaryData = useCallback(async (
        { includeActuals, showTableLoader, showActualsLoader } = { includeActuals: false, showTableLoader: true, showActualsLoader: false }
    ) => {
        if (!selectedForecastId || selectedForecastPeriodId === null) {
            if (showTableLoader) setLoadingTable(false);
            if (showActualsLoader) setLoadingActuals(false);
            return;
        }

        if (showTableLoader) setLoadingTable(true);
        if (showActualsLoader) setLoadingActuals(true);

        try {
            const params = {
                forecastPeriodId: selectedForecastPeriodId,
                aggregationPeriod,
                groupBy
            };
            if (selectedProductIds && Array.isArray(selectedProductIds) && selectedProductIds.length > 0) {
                params.productIds = selectedProductIds.join(",");
            }
            if (selectedBrands && Array.isArray(selectedBrands) && selectedBrands.length > 0) {
                params.brands = selectedBrands.join(",");
            }
            if (selectedProductGroups && Array.isArray(selectedProductGroups) && selectedProductGroups.length > 0) {
                params.productGroups = selectedProductGroups.join(",");
            }
            if (includeActuals) {
                params.includeActuals = true;
                params.actualsCheckpointMode = actualsCheckpointMode;
            }

            const response = await axiosInstance.get(`/forecasts/${selectedForecastId}/outputs-summary`, {
                params
            });
            setSummaryData(response.data);
            if (aggregationPeriod === "weekly") {
                setYtgSourceData(response.data);
            } else {
                try {
                    const weeklyParams = {
                        ...params,
                        aggregationPeriod: "weekly"
                    };
                    const weeklyResponse = await axiosInstance.get(`/forecasts/${selectedForecastId}/outputs-summary`, {
                        params: weeklyParams
                    });
                    setYtgSourceData(weeklyResponse.data);
                } catch (weeklyError) {
                    console.error("Failed to fetch weekly YTG source data:", weeklyError);
                    // Keep primary summary data available even if auxiliary YTG request fails.
                    setYtgSourceData(response.data);
                }
            }
        } catch (error) {
            console.error("Failed to fetch account summary:", error);
            setSummaryData({ channels: [] });
            setYtgSourceData(null);
        } finally {
            if (showTableLoader) setLoadingTable(false);
            if (showActualsLoader || includeActuals) {
                setLoadingActuals(false);
            }
        }
    }, [
        selectedForecastId,
        selectedForecastPeriodId,
        selectedProductIds,
        selectedBrands,
        selectedProductGroups,
        aggregationPeriod,
        groupBy,
        actualsCheckpointMode
    ]);

    const fetchComparisonData = useCallback(async () => {
        if (!compareForecastId || compareForecastPeriodId === null) {
            setComparisonData(null);
            setComparisonYtgSourceData(null);
            setLoadingComparison(false);
            return;
        }

        setLoadingComparison(true);

        try {
            const params = {
                forecastPeriodId: compareForecastPeriodId,
                aggregationPeriod,
                groupBy
            };
            if (selectedProductIds && Array.isArray(selectedProductIds) && selectedProductIds.length > 0) {
                params.productIds = selectedProductIds.join(",");
            }
            if (selectedBrands && Array.isArray(selectedBrands) && selectedBrands.length > 0) {
                params.brands = selectedBrands.join(",");
            }
            if (selectedProductGroups && Array.isArray(selectedProductGroups) && selectedProductGroups.length > 0) {
                params.productGroups = selectedProductGroups.join(",");
            }
            const response = await axiosInstance.get(`/forecasts/${compareForecastId}/outputs-summary`, {
                params
            });
            setComparisonData(response.data);
            if (aggregationPeriod === "weekly") {
                setComparisonYtgSourceData(response.data);
            } else {
                try {
                    const weeklyParams = {
                        ...params,
                        aggregationPeriod: "weekly"
                    };
                    const weeklyResponse = await axiosInstance.get(`/forecasts/${compareForecastId}/outputs-summary`, {
                        params: weeklyParams
                    });
                    setComparisonYtgSourceData(weeklyResponse.data);
                } catch (weeklyError) {
                    console.error("Failed to fetch weekly comparison YTG source data:", weeklyError);
                    // Keep primary comparison data available even if auxiliary YTG request fails.
                    setComparisonYtgSourceData(response.data);
                }
            }
        } catch (error) {
            console.error("Failed to fetch comparison data:", error);
            setComparisonData(null);
            setComparisonYtgSourceData(null);
        } finally {
            setLoadingComparison(false);
        }
    }, [
        compareForecastId,
        compareForecastPeriodId,
        selectedProductIds,
        selectedBrands,
        selectedProductGroups,
        aggregationPeriod,
        groupBy,
        viewSupportsActualsMode
    ]);

    useEffect(() => {
        const includeActuals = viewSupportsActualsMode && showActuals;
        const toggledActuals = previousShowActualsRef.current !== includeActuals;
        previousShowActualsRef.current = includeActuals;

        if (toggledActuals && !includeActuals) {
            setLoadingActuals(false);
        }

        fetchSummaryData({
            includeActuals,
            showTableLoader: !toggledActuals,
            showActualsLoader: toggledActuals && includeActuals
        });
    }, [fetchSummaryData, showActuals, viewSupportsActualsMode, isForecastCompareEnabled]);

    useEffect(() => {
        if (!viewSupportsActualsMode && showActuals) {
            setShowActuals(false);
        }
    }, [viewSupportsActualsMode, showActuals]);

    useEffect(() => {
        if (!isForecastStarted && showActuals) {
            setShowActuals(false);
            setShowCompare(false);
            setLoadingActuals(false);
        }
    }, [isForecastStarted, showActuals]);

    useEffect(() => {
        if (compareExpanded && compareForecastId && compareForecastPeriodId !== null) {
            fetchComparisonData();
        } else {
            setComparisonData(null);
            setComparisonYtgSourceData(null);
        }
    }, [fetchComparisonData, compareExpanded, compareForecastId, compareForecastPeriodId]);

    const handleForecastSelected = (id, period) => {
        if (id === null || id === undefined) {
            localStorage.removeItem('selectedForecast');
            localStorage.setItem('selectedForecastPeriodId', 'null');
            setSelectedForecastId(null);
            setSelectedForecastPeriodId(null);
            return;
        }

        const parsedId = Number(id);
        const normalisedId = Number.isFinite(parsedId) ? parsedId : id;
        localStorage.setItem("selectedForecast", JSON.stringify({ forecastId: normalisedId, startPeriod: period }));
        setSelectedForecastId(normalisedId);

        const storedForecastPeriodId = localStorage.getItem("selectedForecastPeriodId");

        if (storedForecastPeriodId) {
            const forecast = forecasts.find((f) => Number(f.forecastId) === Number(normalisedId));
            const validPeriod = forecast?.forecastPeriods.some(
                (fp) => fp.forecastPeriodId === parseInt(storedForecastPeriodId, 10)
            );
            if (validPeriod) {
                setSelectedForecastPeriodId(parseInt(storedForecastPeriodId, 10));
            } else {
                setSelectedForecastPeriodId(null);
            }
        } else {
            setSelectedForecastPeriodId(null);
        }
    };

    const handleForecastPeriodSelected = (forecastPeriodId) => {
        if (forecastPeriodId === null || forecastPeriodId === undefined) {
            localStorage.setItem('selectedForecastPeriodId', 'null');
            setSelectedForecastPeriodId(null);
            return;
        }
        localStorage.setItem("selectedForecastPeriodId", forecastPeriodId);
        setSelectedForecastPeriodId(forecastPeriodId);
    };

    const handleChangeView = (event) => {
        const newView = event.target.value;
        setView(newView);
        const supportsActuals = supportsActualsMode(newView);
        if (!supportsActuals) {
            setShowActuals(false);
        }
    };

    const handleAggregationPeriodChange = (event, newValue) => {
        setAggregationPeriod(newValue);
    };

    const handleActualsToggle = (event) => {
        const checked = event.target.checked;
        if (checked) {
            setLoadingActuals(true);
        } else {
            setLoadingActuals(false);
            setShowCompare(false);
        }
        setShowActuals(checked);
    };

    const handleActualsCompareToggle = (event) => {
        setShowCompare(event.target.checked);
    };

    const handleActualsCheckpointModeChange = (_event, mode) => {
        if (!mode) {
            return;
        }
        setActualsCheckpointMode(mode);
    };

    const handleGroupByChange = (event, newValue) => {
        if (newValue !== null) {
            setGroupBy(newValue);
        }
    };

    useEffect(() => {
        try {
            localStorage.setItem(GROUP_BY_STORAGE_KEY, groupBy);
        } catch (error) {
            // Ignore storage errors (private mode, disabled storage).
        }
    }, [groupBy]);

    const handleToggleFullscreen = () => {
        setIsTableFullscreen(prev => !prev);
    };

    const shouldShowCheckpointControls = showActuals && !loadingActuals;

    return (
        <Box 
            className="component-container"
            sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                height: '100%',
                overflow: 'hidden'
            }}
        >
            {!isTableFullscreen && (
                <Paper elevation={2} sx={{ p: 3, mb: 2, flexShrink: 0 }}>
                    <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <ForecastDropdown
                                forecasts={forecasts}
                                selectedForecastId={selectedForecastId}
                                onForecastSelected={handleForecastSelected}
                                width={200}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <ForecastPeriodDropdown
                                forecastPeriods={selectedForecast ? selectedForecast.forecastPeriods : []}
                                selectedForecastPeriodId={selectedForecastPeriodId}
                                onForecastPeriodSelected={handleForecastPeriodSelected}
                                width={200}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <FormControl sx={{ minWidth: 200 }}>
                                <InputLabel id="view-select-label">View</InputLabel>
                                <Select 
                                    labelId="view-select-label"
                                    label="View"
                                    onChange={handleChangeView} 
                                    value={view}
                                >
                                    <MenuItem value="units">Units</MenuItem>
                                    <MenuItem value="grossRev">Gross Revenue</MenuItem>
                                    <MenuItem value="overlay">Overlay</MenuItem>
                                    <MenuItem value="netRev">Net Revenue</MenuItem>
                                    <MenuItem value="triggers">Triggers</MenuItem>
                                    <MenuItem value="fixedSpend">Fixed Spend</MenuItem>
                                    <MenuItem value="grossProfit">Gross Profit</MenuItem>
                                    <MenuItem value="grossProfitPercent">Gross Profit Percent</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <ToggleButtonGroup
                                value={groupBy}
                                exclusive
                                onChange={handleGroupByChange}
                                aria-label="group by"
                                size="medium"
                            >
                                <ToggleButton value="sku" aria-label="sku">
                                    SKU
                                </ToggleButton>
                                <ToggleButton value="customer" aria-label="customer">
                                    Customer
                                </ToggleButton>
                                <ToggleButton value="list" aria-label="list">
                                    List
                                </ToggleButton>
                            </ToggleButtonGroup>
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <ProductFiltersButton
                            filtersExpanded={filtersExpanded}
                            onToggle={handleFiltersToggle}
                            disabled={loadingForecasts}
                        />
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <CompareButton
                            compareExpanded={compareExpanded}
                            onToggle={handleCompareToggle}
                            disabled={loadingForecasts}
                        />
                    </Grid>
                    {viewSupportsActualsMode && !compareExpanded && isForecastStarted && (
                        <>
                            <Grid item xs={12} sm="auto">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={showActuals}
                                            onChange={handleActualsToggle}
                                            disabled={loadingForecasts || loadingTable || !hasValidSelection}
                                            inputProps={{ 'aria-label': 'toggle actuals' }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            Actuals
                                            {loadingActuals && <CircularProgress size={16} sx={{ ml: 1.25 }} />}
                                        </Box>
                                    }
                                    labelPlacement="end"
                                    sx={{ m: 0 }}
                                />
                            </Grid>
                            {shouldShowCheckpointControls && (
                                <Grid item xs={12} sm="auto">
                                    <FormControl size="small" sx={{ minWidth: 170 }}>
                                        <InputLabel id="actuals-up-to-label">Up to</InputLabel>
                                        <Select
                                            labelId="actuals-up-to-label"
                                            label="Up to"
                                            value={actualsCheckpointMode}
                                            onChange={(event) => handleActualsCheckpointModeChange(event, event.target.value)}
                                            inputProps={{ 'aria-label': 'up to' }}
                                        >
                                            <MenuItem value={ACTUALS_CHECKPOINT_MODES.now}>Last week</MenuItem>
                                            <MenuItem value={ACTUALS_CHECKPOINT_MODES.latestOrder}>Latest sales order</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            {showActuals && !loadingActuals && (
                                <Grid item xs={12} sm="auto">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                size="small"
                                                checked={showCompare}
                                                onChange={handleActualsCompareToggle}
                                                disabled={loadingForecasts || loadingTable || !hasValidSelection}
                                                inputProps={{ 'aria-label': 'toggle compare' }}
                                            />
                                        }
                                        label="Compare"
                                        labelPlacement="end"
                                        sx={{ m: 0 }}
                                    />
                                </Grid>
                            )}
                        </>
                    )}
                    </Grid>
                </Paper>
            )}

            {!isTableFullscreen && (
                <Box sx={{ flexShrink: 0 }}>
                    <ProductFilters
                    selectedForecastId={selectedForecastId}
                    selectedForecastPeriodId={selectedForecastPeriodId}
                    selectedBrands={selectedBrands}
                    setSelectedBrands={setSelectedBrands}
                    selectedProductGroups={selectedProductGroups}
                    setSelectedProductGroups={setSelectedProductGroups}
                    selectedProductIds={selectedProductIds}
                    setSelectedProductIds={setSelectedProductIds}
                    filtersExpanded={filtersExpanded}
                    onToggle={handleFiltersToggle}
                    selectedCustomerId={null}
                    selectedChannelId={null}
                />
                </Box>
            )}

            {!isTableFullscreen && (
                <Box sx={{ flexShrink: 0 }}>
                    <CompareFilters
                    compareExpanded={compareExpanded}
                    forecasts={forecasts}
                    loadingForecasts={loadingForecasts}
                    compareForecastId={compareForecastId}
                    compareForecastPeriodId={compareForecastPeriodId}
                    onCompareForecastSelected={handleCompareForecastSelected}
                    onCompareForecastPeriodSelected={handleCompareForecastPeriodSelected}
                />
                </Box>
            )}

            {selectedForecastId && selectedForecastPeriodId !== null && selectedForecastPeriodId > 0 && (
                <Box 
                    sx={{ 
                        flexGrow: 1, 
                        display: 'flex', 
                        flexDirection: 'column',
                        minHeight: 0,
                        overflow: 'hidden'
                    }}
                >
                    {!isTableFullscreen && (
                        <Paper elevation={1} sx={{ mb: 2, flexShrink: 0 }}>
                            <Tabs 
                                value={aggregationPeriod} 
                                onChange={handleAggregationPeriodChange}
                                sx={{ borderBottom: 1, borderColor: 'divider' }}
                            >
                                <Tab label="Monthly" value="monthly" />
                                <Tab label="Quarterly" value="quarterly" />
                                <Tab label="Weekly" value="weekly" />
                            </Tabs>
                        </Paper>
                    )}
                    {loadingTable ? (
                        <Skeleton variant="rectangular" width="100%" sx={{ flexGrow: 1 }} />
                    ) : (
                        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                            <OutputsSummaryTable 
                                summaryData={summaryData} 
                                view={view} 
                                startMonth={summaryData?.startMonth || 1} 
                                selectedForecast={selectedForecast}
                                aggregationPeriod={aggregationPeriod}
                                showActuals={showActuals}
                                loadingActuals={loadingActuals}
                                showCompare={showCompare}
                                groupBy={groupBy}
                                comparisonData={comparisonData}
                                ytgSourceData={ytgSourceData}
                                comparisonYtgSourceData={comparisonYtgSourceData}
                                comparisonForecast={comparisonForecast}
                                loadingComparison={loadingComparison}
                                isComparingForecasts={compareExpanded && compareForecastId && compareForecastPeriodId !== null}
                                onToggleFullscreen={handleToggleFullscreen}
                                isFullscreen={isTableFullscreen}
                            />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ForecastOutputsSummary;
